import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { Fragment, useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate } from 'react-router'
import { ButtonPrimary, ButtonRedSecondary } from '../../../../../../../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import SingleFormLayout from '../../../../../../../../../baseComponents/Layout/SingleFormLayout'
import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import ModalChangeActiveCustomer from '../../../../../../../../../specificComponents/Customers/ModalChangeActiveCustomerCustomer'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import locations from '../../../../../../../../../utils/globals/locations'
import { valueOrDefaultValue } from '../../../../../../../../../utils/globals/valueOrDefaultValue'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import useEditDeleteModal from '../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { useTypeIdNumber } from '../../../../../../../../../utils/hooks/useTypeIdNumber'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../../../../../utils/reducers/company/employees'
import { getActiveLangs, getAllClientManagerEmployees, getConfigParam } from '../../../../../../../../../utils/reducers/getters'
import ModalContactChangeType from './ModalContactB2BChangeType'

type MainContactB2BProfileProps = ComponentWithPermissions & ReduxMainContactB2BProfile & {
    basicData: CustomerModelType | null
    getData: () => Promise<void>
    extraProps?: TwinDictionary
}

interface CustomerModelTypeExtended extends CustomerModelType {
    transform_to_client: string
}

const MainContactB2BProfile: React.FC<MainContactB2BProfileProps> = ({ basicData, userPermissions, getData, extraProps, employees }) => {
    const { t } = useTwinTranslation()
    const { typesIdNumber } = useTypeIdNumber()
    const { openModal, setOpenModal } = useMainContactB2BProfileLogic()
    const navigate = useNavigate()
    const showAllCustomersTogether = getConfigParam('showAllCustomersTogether') === 'on' ? true : false
    const myEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}))), [employees])


    const fields: FormRender<CustomerModelTypeExtended> = [
        {
            cols: 2,
            title: t('generalInformation', 'Información general'),
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'fiscal_name',
                    label: t('fiscalName', 'Nombre fiscal'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'type_id_number',
                    label: t('typeIdNumber', 'Tipo de documento identificación'),
                    component: 'CustomSelect',
                    items: typesIdNumber,
                    value: basicData?.type_id_number,
                },
                {
                    name: 'id_number',
                    label: t('id_number', 'DNI'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'code',
                    label: t('customerCode', 'Código de Cliente'),
                    component: 'InputWithLabelMarginIntegerStateFull',
                },
                {
                    name: 'customer_card',
                    label: t('customerCard', 'Tarjeta de cliente'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'responsable_name',
                    label: t('responsableName', 'Nombre del responsable'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'ResponsibleEmployeeId',
                    label: t('ourManager', 'Nuestro gestor'),
                    component: 'CustomSelectWithSearchBar',
                    items: myEmployees || {},
                    fieldName: 'fullname_short',
                    sortItems: true
                }
            ]
        },
        {
            cols: 2,
            title: t('contactInformation', 'Datos de contacto'),
            elements: [
                {
                    name: 'phone',
                    label: t('phone', 'Teléfono'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'mobile',
                    label: t('mobile', 'Móvil'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'email',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'email',
                },
                {
                    name: 'web',
                    label: t('web', 'Página web'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                },
                {
                    name: 'CustomerOriginId',
                    label: t('origin', 'Origen'),
                    component: 'CustomSelectWithSearchBar',
                    items: extraProps?.dictCustomerOrigins || {},
                },
                {
                    name: 'newsletter',
                    label: t('newsletter', 'Newsletter'),
                    component: 'CheckboxMargin',
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: basicData?.id
                }
            ]
        },
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, basicData)

    const extraButtons = [
        <ButtonRedSecondary className='regular14' onClick={() => setOpenModal({ type: 'delete', allRowData: {} })}><TwinTrans transKey='delete'>Eliminar</TwinTrans></ButtonRedSecondary>,
        <ButtonPrimary key='transformToClient' className='regular14' onClick={() => setOpenModal({ type: 'create', allRowData: {} })}><TwinTrans transKey='saveAndTransformToCustomerB2B'>Guardar y Transformar a cliente B2B</TwinTrans></ButtonPrimary>
    ]
    return (
        <Fragment>
            <SingleFormLayout action='/api/app/customer/updateCustomer' onSubmit={getData} sections={parsedFields} userPermissions={userPermissions} extraButtonsLeft={extraButtons}>
            </SingleFormLayout>
            {openModal?.type === 'create' ? <ModalContactChangeType setOpened={() => setOpenModal(null)} onSubmit={getData} allRowData={basicData || {}} /> : null}
            {openModal?.type === 'delete' ? <ModalChangeActiveCustomer {...{ setOpened: () => setOpenModal(null) }} allRowData={basicData || {}} onSubmit={() => navigate(locations.customer + '/' + (showAllCustomersTogether ? 'allCustomers' : 'contactsB2B'))} /> : null}
        </Fragment>
    )
}

const useMainContactB2BProfileLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return { openModal, setOpenModal }
}


const mainContactB2BProfileDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxMainContactB2BProfile = ConnectedProps<typeof mainContactB2BProfileConnect>
const mapMainContactB2BProfileTypesConnector = (state: AllReduxPayloads) => ({ employees: state.employees })
const mainContactB2BProfileConnect = connect(mapMainContactB2BProfileTypesConnector, mainContactB2BProfileDispatch)

const mainContactB2BProfileConnectLoading = withLoading(MainContactB2BProfile, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])

const ModalCreateMainContactB2BProfileConnect = mainContactB2BProfileConnect(mainContactB2BProfileConnectLoading)


export default ModalCreateMainContactB2BProfileConnect
